import React, { useState, useEffect, useRef, useCallback } from 'react';
import API_URL from '../api';
import { Link } from 'react-router-dom';
import { ParallaxProvider } from 'react-scroll-parallax';

// Font styles (copied from Home.jsx for consistency)
const fontStyle = `
  @font-face {
    font-family: 'ArquitectaBold';
    src: url('/fonts/ArquitectaBold-webfont.eot');
    src: url('/fonts/ArquitectaBold-webfont.eot?#iefix') format('embedded-opentype'),
         url('/fonts/ArquitectaBold-webfont.woff') format('woff'),
         url('/fonts/ArquitectaBold-webfont.ttf') format('truetype'),
         url('/fonts/ArquitectaBold-webfont.svg#arquitecta_boldregular') format('svg');
    font-weight: normal;
    font-style: normal;
  }
  
  @font-face {
    font-family: 'CourierPrime';
    src: url('/fonts/courier_prime-webfont.woff') format('woff');
    font-weight: normal;
    font-style: normal;
  }
  
  @font-face {
    font-family: 'CourierPrimeItalic';
    src: url('/fonts/courier_prime_italic-webfont.woff') format('woff');
    font-weight: normal;
    font-style: italic;
    font-display: swap;
  }

  @font-face {
    font-family: 'BebasNeue';
    src: url('/fonts/bebasneue-webfont.woff') format('woff');
    font-weight: normal;
    font-style: normal;
    font-display: swap;
  }
`;

const styleElement = document.createElement('style');
styleElement.textContent = fontStyle;
document.head.appendChild(styleElement);

const MENU_LINKS = [
  { label: 'Home', href: '/' },
  { label: 'About', href: '/about' },
  { label: 'Directors', href: '/directors' }, // This will be a dropdown trigger
  { label: 'Contact', href: '/contact' },
];

const pageContainerStyle = {
  paddingTop: '80px', // Adjust if menu height is different
  fontFamily: 'CourierPrime, monospace', // Apply CourierPrime as base font
};

const headerStyle = {
  textAlign: 'center',
  marginBottom: '30px',
  fontSize: '2.5em',
  color: '#333',
  fontFamily: 'BebasNeue, sans-serif', // Example: Use BebasNeue for headers if desired
};

const contentStyle = {
  background: '#ffffff', // White background for content area
  padding: '30px',
  borderRadius: '8px',
  // boxShadow: '0 2px 4px rgba(0,0,0,0.1)', // Optional shadow
  maxWidth: '900px',
  margin: '0 auto 40px auto',
  fontFamily: 'CourierPrime, monospace', // Ensure content uses CourierPrime
  lineHeight: '1.8',
  color: '#333333',
};

const loadingErrorStyle = {
  textAlign: 'center',
  fontSize: '1.2em',
  color: '#cc0000',
  marginTop: '50px',
  fontFamily: 'CourierPrime, monospace',
};

export default function AboutPage() {
  const [aboutContent, setAboutContent] = useState('');
  const [lastUpdated, setLastUpdated] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Menu state and refs (copied from Home.jsx)
  const [directors, setDirectors] = useState([]);
  const menuRef = useRef(null);
  const menuButtonRef = useRef(null);
  const menuCloseTimeout = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [isMenuHovered, setIsMenuHovered] = useState(false);
  const [menuHeight, setMenuHeight] = useState(0);

  // Fetch About Page Content
  useEffect(() => {
    async function fetchAboutContent() {
      setLoading(true);
      try {
        const response = await fetch(`${API_URL}/about-content`);
        if (!response.ok) {
          throw new Error(`Failed to fetch about content: ${response.status} ${response.statusText}`);
        }
        const data = await response.json();
        setAboutContent(data || { content: '<p>No content available.</p>', title: 'About Us', header_image_url: null });
        setLastUpdated(data.last_updated || '');
      } catch (err) {
        console.error('Error fetching about content:', err);
        setError(err.message);
        setAboutContent({ content: '<p>Could not load content.</p>', title: 'Error Loading Content', header_image_url: null });
      } finally {
        setLoading(false);
      }
    }
    fetchAboutContent();
  }, []);

  // Fetch directors for menu (copied from Home.jsx)
  useEffect(() => {
    async function fetchAllDirectors() {
      const apiUrl = `${API_URL}/directors`;
      try {
        const res = await fetch(apiUrl);
        if (!res.ok) throw new Error(`Failed to fetch directors: ${res.status}`);
        const data = await res.json();
        setDirectors(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Error in fetchAllDirectors:', err);
        setDirectors([]);
      }
    }
    fetchAllDirectors();
  }, []);

  // Measure menu height (copied from Home.jsx)
  useEffect(() => {
    if (menuRef.current && menuOpen) {
      setMenuHeight(menuRef.current.offsetHeight + 125); 
    }
  }, [menuOpen, directors]);

  // Close menu on click outside or Escape (copied from Home.jsx)
  useEffect(() => {
    function handleClickOutside(event) {
      if (menuOpen && menuRef.current && menuButtonRef.current &&
          !menuRef.current.contains(event.target) &&
          !menuButtonRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    }
    function handleEscapeKey(event) {
      if (event.key === 'Escape') {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
      clearTimeout(menuCloseTimeout.current);
    };
  }, [menuOpen]);

  // Menu hover handlers from Home.jsx
  const handleMenuEnter = useCallback(() => {
    clearTimeout(menuCloseTimeout.current);
    setIsMenuHovered(true);
    // setMenuOpen(true); // Optional: open menu fully on hover, or let click/button hover handle it
  }, []);

  const handleMenuLeave = useCallback(() => {
    menuCloseTimeout.current = setTimeout(() => {
      if (!isHovering) { // isHovering refers to the button/logo area
        setIsMenuHovered(false);
        if (!isHovering) setMenuOpen(false); // Close if not hovering button area either
      }
    }, 300);
  }, [isHovering]);

  const handleButtonEnter = useCallback(() => {
    clearTimeout(menuCloseTimeout.current);
    setIsHovering(true);
    setMenuOpen(true); // Open menu when hovering button/logo area
  }, []);

  const handleButtonLeave = useCallback(() => {
    setIsHovering(false);
    menuCloseTimeout.current = setTimeout(() => {
      if (!isMenuHovered) {
        setMenuOpen(false);
      }
    }, 300);
  }, [isMenuHovered]);

  if (loading && !aboutContent) { // Show loading only if content isn't already there (e.g. from cache)
    return <div style={loadingErrorStyle}>Loading About Page...</div>;
  }

  // Error display can be part of the page layout if menu is to be shown
  // if (error && !aboutContent) {
  //   return <div style={loadingErrorStyle}>Error: {error}</div>;
  // }

  const headerImageStyle = {
    width: '100%', // Make image responsive
    height: 'auto',
    marginBottom: '20px', // Add some space below the image
    borderRadius: '8px', // Optional: add some rounded corners
    objectFit: 'cover' // Ensures the image covers the area, might crop
  };

  return (
    <ParallaxProvider 
      smoothScroll={true} 
      smoothScrollDuration={800}
      scrollAxis="vertical"
    >
      <div style={{ minHeight: '100vh', width: '100%', backgroundColor: '#000000' }}>
        <div style={pageContainerStyle}>
      {/* Navigation Menu from Home.jsx */}
      <div 
          style={{
            position: 'fixed',
            top: '20px',
            left: '50%',
            transform: menuOpen ? `translate(-50%, ${menuHeight}px)` : 'translate(-50%, 0)', // Synced with Home.jsx
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            gap: '20px',
            transition: 'transform 0.3s ease-in-out',
            transformOrigin: 'center top',
            willChange: 'transform',
            pointerEvents: 'none'
          }}
        >
          {/* Hamburger Menu Button */}
          <div 
            ref={menuButtonRef}
            onClick={() => setMenuOpen(!menuOpen)} // Simple toggle
            onMouseEnter={handleButtonEnter}
            onMouseLeave={handleButtonLeave}
            style={{
              cursor: 'pointer',
              width: '24px',
              height: '20px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              padding: '5px 0',
              boxSizing: 'content-box',
              pointerEvents: 'auto'
            }}
          >
            <span style={{
              display: 'block',
              width: '100%',
              height: '2px',
              backgroundColor: '#fff', // Changed to white for dark page background
              transition: 'all 0.3s ease',
              transform: menuOpen ? 'rotate(45deg) translate(5px, 5px)' : 'none'
            }}></span>
            <span style={{
              display: 'block',
              width: '100%',
              height: '2px',
              backgroundColor: '#fff', // Changed to white for dark page background
              transition: 'all 0.3s ease',
              opacity: menuOpen ? 0 : 1,
              transform: menuOpen ? 'translateX(20px)' : 'none'
            }}></span>
            <span style={{
              display: 'block',
              width: '100%',
              height: '2px',
              backgroundColor: '#fff', // Changed to white for dark page background
              transition: 'all 0.3s ease',
              transform: menuOpen ? 'rotate(-45deg) translate(5px, -5px)' : 'none'
            }}></span>
          </div>

          {/* Page Title in Navbar */}
          <Link to="/" style={{
            display: 'block',
            height: 'auto',
            transition: 'transform 0.3s ease-in-out',
            pointerEvents: 'auto',
            textDecoration: 'none',
            color: '#fff',
            fontSize: '28px',
            fontFamily: 'CourierPrime, monospace',
            fontWeight: 'normal', // Adjusted from bold to normal for BebasNeue
            lineHeight: '1', // Ensure consistent vertical alignment
            paddingTop: '2px' // Fine-tune vertical alignment if needed
          }}>
            {aboutContent ? aboutContent.title : 'About Us'}
          </Link>
        </div>

        {/* Menu Panel from Home.jsx */}
        <div 
          ref={menuRef}
          style={{
            position: 'fixed',
            top: '0',
            left: '0',
            width: '100%',
            backgroundColor: '#fff',
            zIndex: 998,
            padding: '40px 0 10px',
            opacity: menuOpen ? 1 : 0,
            pointerEvents: menuOpen ? 'auto' : 'none',
            transform: menuOpen ? 'translateY(0)' : 'translateY(-100%)',
            transition: 'all 0.3s ease-in-out',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden',
            maxHeight: menuOpen ? '100vh' : '0px', // Control height for transition
            visibility: menuOpen ? 'visible' : 'hidden',
          }}
          onMouseEnter={handleMenuEnter} // Use the new/copied handler
          onMouseLeave={handleMenuLeave} // Use the new/copied handler
        >
          <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
            <div style={{ marginBottom: '15px', textAlign: 'center' }}>
              <h3 style={{ fontSize: '12px', fontWeight: 'normal', textTransform: 'uppercase', letterSpacing: '2px', margin: '0 0 15px', color: '#666', fontFamily: 'BebasNeue, sans-serif' }}>Directors</h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '10px', marginBottom: '10px' }}>
                {directors.filter(d => d.active).map(director => (
                  <Link 
                    key={director.id} 
                    to={`/directors/${director.id}`} 
                    onClick={() => setMenuOpen(false)}
                    style={{ color: '#333', textDecoration: 'none', fontSize: '20px', margin: '0 8px', fontWeight: 'normal', fontFamily: 'BebasNeue, sans-serif', lineHeight: '1.2', transition: 'color 0.2s', opacity: 0.8 }}
                    onMouseOver={e => e.currentTarget.style.opacity = '1'}
                    onMouseOut={e => e.currentTarget.style.opacity = '0.8'}
                  >
                    {director.first_name} {director.last_name}
                  </Link>
                ))}
              </div>
              <div style={{ width: '100%', height: '1px', backgroundColor: '#eee', margin: '15px auto', maxWidth: '500px' }}></div>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px 20px', marginBottom: '10px', justifyContent: 'center', maxWidth: '800px', marginLeft: 'auto', marginRight: 'auto', padding: '0 20px' }}>
              {MENU_LINKS.filter(link => link.label !== 'Directors').map(link => (
                <Link 
                  key={link.label} 
                  to={link.href} 
                  onClick={() => setMenuOpen(false)}
                  style={{ color: 'rgba(0,0,0,0.8)', textDecoration: 'none', fontSize: '16px', fontFamily: 'BebasNeue, sans-serif', textTransform: 'uppercase', letterSpacing: '1px', padding: '3px 0', position: 'relative', transition: 'color 0.2s' }}
                  onMouseOver={e => e.currentTarget.style.color = 'rgba(0,0,0,1)'}
                  onMouseOut={e => e.currentTarget.style.color = 'rgba(0,0,0,0.8)'}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </div>

      {/* Main Page Content */}
      <main className="pt-10" style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
        {error && (
            <div style={{...loadingErrorStyle, color: 'red', marginBottom: '20px'}}>Error: {error}</div>
        )}

        <div 
          className="about-content-container max-w-4xl px-4" 
          style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
        >
          {aboutContent && aboutContent.header_image_url && (
            <img 
              src={`${API_URL.replace('/api', '')}${aboutContent.header_image_url}`} 
              alt={aboutContent.title || 'About Us Header'} 
              style={headerImageStyle} 
            />
          )}
          {aboutContent && aboutContent.content && (
            <div 
              dangerouslySetInnerHTML={{ __html: aboutContent.content }}
              style={{ color: '#fff', width: '100%' }} // Force white text and ensure full width for left-align
            />
          )}
        </div> {/* End of about-content-container */}

      </main> {/* End of main */}
    </div> {/* End of pageContainerStyle div */}
  </div> {/* End of minHeight: '100vh' div */}
</ParallaxProvider>
);
}
